// External
import _ from "lodash";

/**
 * Compare two column entries inside gradingPeriodWise[period].config[col].
 * Only fields present on BOTH sides are compared. The UI may omit derived
 * metadata (e.g. `type`) that the inherited-default builder includes;
 * user intent lives in the overlapping fields (isEnabled, label, etc).
 */
const isColumnConfigEquivalent = (a, b) => {
  const commonFields = _.intersection(_.keys(a || {}), _.keys(b || {}));
  return _.every(commonFields, f => _.isEqual(_.get(a, f), _.get(b, f)));
};

/**
 * Compare two gradingPeriodWise[period] entries.
 * displayOrder must match exactly. Column configs are compared only for
 * columns that exist on both sides (added/removed columns are ignored —
 * they reflect structural differences, not user-intent diffs).
 */
const isGradingPeriodEntryEquivalent = (a, b) => {
  if (!_.isEqual(_.get(a, "displayOrder"), _.get(b, "displayOrder"))) {
    return false;
  }
  const aConfig = _.get(a, "config", {});
  const bConfig = _.get(b, "config", {});
  const commonCols = _.intersection(_.keys(aConfig), _.keys(bConfig));
  return _.every(commonCols, c =>
    isColumnConfigEquivalent(aConfig[c], bConfig[c])
  );
};

/**
 * Compare two transcript configurations semantically.
 *
 * The comparison is "loose" in the following ways, so that UI rebuilds
 * and derived metadata don't cause spurious mismatches:
 *   - The `isOverridden` flag is stripped from both sides.
 *   - Inside `gradeScales.gradingPeriodWise`, only period ids present on
 *     both sides are compared (added/removed periods are ignored).
 *   - Inside each period's column configs, only fields present on both
 *     sides are compared.
 *
 * All other top-level fields (general, columnAggregates, rowAggregates,
 * yearAggregates, the non-gradingPeriodWise parts of gradeScales) must
 * deep-equal.
 */
export const isConfigurationEquivalent = (a, b) => {
  if (_.isEmpty(a) && _.isEmpty(b)) return true;
  if (_.isEmpty(a) || _.isEmpty(b)) return false;

  const omitGradingPeriodWise = configuration => ({
    ..._.omit(configuration, ["isOverridden"]),
    gradeScales: _.omit(_.get(configuration, "gradeScales", {}), [
      "gradingPeriodWise",
    ]),
  });
  if (!_.isEqual(omitGradingPeriodWise(a), omitGradingPeriodWise(b))) {
    return false;
  }

  const aGradingPeriodWise = _.get(a, "gradeScales.gradingPeriodWise", {});
  const bGradingPeriodWise = _.get(b, "gradeScales.gradingPeriodWise", {});
  const commonPeriodIds = _.intersection(
    _.keys(aGradingPeriodWise),
    _.keys(bGradingPeriodWise)
  );
  return _.every(commonPeriodIds, periodId =>
    isGradingPeriodEntryEquivalent(
      aGradingPeriodWise[periodId],
      bGradingPeriodWise[periodId]
    )
  );
};

/**
 * Handles default configuration updates
 */
export const handleDefaultConfigurationUpdate = ({
  update,
  academicRecordsData,
}) => {
  const incomingConfiguration = _.cloneDeep(_.get(update, "configuration", {}));
  const originalDefaultConfiguration = _.get(
    academicRecordsData,
    "node.transcriptSummary.configuration",
    {}
  );

  return {
    ...update,
    configuration: incomingConfiguration,
    isDirty: !_.isEqual(incomingConfiguration, originalDefaultConfiguration),
  };
};

/**
 * Main handler for grade configuration updates.
 *
 * Semantics per the Display Settings invariants:
 *   - The stored configuration preserves disabled-GP configs across
 *     re-renders by merging backend → prev → incoming at the
 *     gradingPeriodWise level. Incoming wins for periods it has
 *     (applies real user edits including column removals); prev / backend
 *     fill in periods the UI dropped because the GP was disabled.
 *   - `isDirty` = the effective grade configuration differs from the
 *     grade-level inherited default (not the backend-saved configuration).
 *     For backend-overridden grades this is true on first visit even
 *     without user edits; the save logic handles this as an idempotent
 *     no-op (emits current config with isOverridden:true).
 *   - `isResetToDefault` = backend is currently overridden AND the effective
 *     configuration matches the default-draft (org-level) configuration.
 *     When set, the save payload emits `configuration: null` for the grade
 *     to let the backend clear its override.
 */
export const handleGradeConfigurationUpdate = ({
  update,
  prevUpdate,
  academicRecordsData,
  getBackendIsOverridden,
  // Called as ({ selectedGradeId, defaultDraftConfiguration, ... }) and
  // returns the grade-level inherited default (same shape as a grade
  // configuration: gradingPeriodWise keyed by current period ids). This
  // is the correct baseline for the "back to default" check when the
  // backend is overridden — the raw org default has a different shape
  // and can't be compared directly to a grade config.
  getResetDraftConfiguration,
  defaultDraftConfiguration,
  orgGradeScalesData,
  curriculumProgramsData,
  // Effective GP list for this grade (draft if present, else backend).
  // Used to build the baseline with entries for every period currently
  // in scope — including locally-added ones — so user customizations on
  // those new periods are detected as dirty.
  effectiveGradingPeriods,
}) => {
  const key = _.get(update, "key");
  const gradeMapId = _.get(update, "selectedGradeId", key);
  const rawIncomingConfiguration = _.cloneDeep(
    _.get(update, "configuration", {})
  );
  const backendIsOverridden = getBackendIsOverridden(gradeMapId);

  const gradeMap = _.find(
    _.get(academicRecordsData, "node.transcriptSummary.grades", []),
    g => _.get(g, "id") === gradeMapId
  );
  const backendConfiguration = _.get(gradeMap, "configuration", null);
  const backendPeriodWise = _.get(
    backendConfiguration,
    "gradeScales.gradingPeriodWise",
    {}
  );
  const gradeLevelDefault = getResetDraftConfiguration
    ? getResetDraftConfiguration({
        academicRecordsData,
        selectedGradeId: gradeMapId,
        defaultDraftConfiguration,
        orgGradeScalesData,
        curriculumProgramsData,
        effectivePeriods: effectiveGradingPeriods,
      })
    : null;

  const prevPeriodWise = _.get(
    prevUpdate,
    "configuration.gradeScales.gradingPeriodWise",
    {}
  );
  const incomingPeriodWise = _.get(
    rawIncomingConfiguration,
    "gradeScales.gradingPeriodWise",
    {}
  );
  const mergedPeriodWise = {
    ...backendPeriodWise,
    ...prevPeriodWise,
    ...incomingPeriodWise,
  };

  const configuration = _.isEqual(mergedPeriodWise, incomingPeriodWise)
    ? rawIncomingConfiguration
    : {
        ...rawIncomingConfiguration,
        gradeScales: {
          ..._.get(rawIncomingConfiguration, "gradeScales", {}),
          gradingPeriodWise: mergedPeriodWise,
        },
      };

  // isDirty = "local config differs from what the default-for-the-current-
  // selection would be". Using gradeLevelDefault (built with the effective
  // period list) as the baseline — rather than backendConfiguration —
  // means customizations on locally-added GPs are detected as dirty and
  // get included in the save payload. For backend-overridden grades the
  // initial load produces isDirty=true (local = backend ≠ default), which
  // is fine: the save logic will see isResetToDefault=false and emit the
  // current config with isOverridden:true (idempotent no-op vs backend).
  const isDirty =
    !_.isEmpty(gradeLevelDefault) &&
    !isConfigurationEquivalent(configuration, gradeLevelDefault);
  const isResetToDefault =
    backendIsOverridden &&
    !_.isEmpty(gradeLevelDefault) &&
    isConfigurationEquivalent(configuration, gradeLevelDefault);

  return {
    ...prevUpdate,
    ...update,
    configuration,
    isDirty,
    isResetToDefault,
  };
};
